import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCallback, memo, useMemo, useEffect, useState } from 'react';
import moment from 'moment';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';

import ModalContainer from '../common/modal-container/modal-container';
import { logData } from '../../utils/debug.util';
import { RootState } from '../../store/rootReducer';
import useGoogleRoutes from '../../hooks/useGoogleRoutes/useGoogleRoutes';
import { theme } from '../../global-styles/colors';
import { GOOGLE_API_KEY, MOSTRAR_RUTA } from '../../constants/env';
import { INSURANCE_COMPANIES } from '../../constants';
import GeolocationIcon from '../../assets/iconsComponent/GeolocationIcon';
import { markerIcons, craneIcons, taxiIcons } from '../../assets/icons/icons';
import { fetchLocationByCoords } from '../../api/fetchLocation';
import { IGoogleMapsProps, LocationOption } from './google-maps-types';
import { CenterMarkerStyle, GeolocationButtonStyle, GoogleMapsStyles, OverlayViewDiv } from './google-maps-styles';
import { GOOGLE_MAPS_LIBRARIES } from './GoogleMapsLibraries';

const containerStyle = {
  height: '250px',
  width: '100%',
};

const DEFAULT_ZOOM = 15;

const options: google.maps.MapOptions = {
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: true,
  fullscreenControl: true,
  styles: null,
};

const libraries = GOOGLE_MAPS_LIBRARIES;

export const DEFAULT_LATITUDE = 40.42293153465378;
export const DEFAULT_LONGITUDE = -3.7043371537897305;
export const DEFAULT_ADDRESS = 'C. de San Roque, 20, Centro, 28004 Madrid, España';

const GoogleMaps: React.FC<IGoogleMapsProps> = ({
  coords,
  zoom,
  myCrane,
  craneLocations,
  onCraneClick,
  mapStyles,
  markerList,
  updateLocationFn,
  showCenter,
  autoLocate,
  myTaxi,
  showLocation,
}) => {
  const { t } = useTranslation(['components']);

  const [currentCoords, setCurrentCoords] = useState(coords);

  const provider = useSelector((state: RootState) => state.general.provider);
  const strokeColor = (provider && INSURANCE_COMPANIES?.[provider]?.theme.primary.primary50) || theme.primary.primary50;
  const { getRoute, renderRoute } = useGoogleRoutes(strokeColor);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_API_KEY || '',
    language: 'es',
    libraries: libraries,
  });

  if (mapStyles) options.styles = mapStyles;
  else options.styles = null;

  const [arrivalTime, setArrivalTime] = useState<moment.Moment | undefined>();
  const [gmap, setGmap] = useState<google.maps.Map | undefined>();
  const [isLocating, setIsLocating] = useState(false);
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);
  const [showPermissionInfo, setShowPermissionInfo] = useState(false);
  const [myCraneLocation, setMyCraneLocation] = useState<google.maps.LatLngLiteral | undefined>();
  const [myTaxiLocation, setMyTaxiLocation] = useState<google.maps.LatLngLiteral | undefined>();
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);

  const currentCenter = useMemo(() => {
    return currentCoords
      ? { lat: currentCoords[0], lng: currentCoords[1] }
      : { lat: DEFAULT_LATITUDE, lng: DEFAULT_LONGITUDE };
  }, [currentCoords]);

  const myCraneCoords = useMemo(() => {
    return myCrane && Array.isArray(myCrane) && myCrane?.length === 2
      ? { lat: myCrane[0], lng: myCrane[1] }
      : undefined;
  }, [myCrane]);

  const onUnmount = useCallback(() => {}, []);

  useEffect(() => {
    if (!MOSTRAR_RUTA) return;
    const fetchRoute = async () => {
      if (gmap && myCraneCoords && currentCenter) {
        const result = await getRoute(myCraneCoords, currentCenter, setArrivalTime);
        setDirections(result);
      }
    };
    void fetchRoute();
  }, [gmap, myCraneCoords, currentCenter, getRoute]);

  useEffect(() => {
    if (!MOSTRAR_RUTA) return;
    if (directions && gmap) {
      renderRoute(directions, gmap ?? null);
    }
  }, [renderRoute, directions, gmap]);

  useEffect(() => {
    if (myCrane && Array.isArray(myCrane) && myCrane?.length === 2)
      setMyCraneLocation({ lat: myCrane[0], lng: myCrane[1] });
  }, [myCrane]);

  useEffect(() => {
    if (myTaxi && Array.isArray(myTaxi) && myTaxi?.length === 2) setMyTaxiLocation({ lat: myTaxi[0], lng: myTaxi[1] });
  }, [myTaxi]);

  const updateLocation = async (coords: google.maps.LatLng) => {
    if (updateLocationFn && coords) {
      const response = await fetchLocationByCoords(coords);

      const newLocation: LocationOption = {
        label: '',
        formattedAddress: response?.results ? response.results[0].formatted_address : '',
        value: [coords.lat(), coords.lng()],
      };

      updateLocationFn(newLocation);
    }
  };

  const geolocateOptions: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
  };

  const onSuccess: PositionCallback = (position) => {
    setCurrentCoords([position.coords.latitude, position.coords.longitude]);
    setIsLocating(false);
    setShowPermissionInfo(false);
    updateLocation(new google.maps.LatLng(position.coords.latitude, position.coords.longitude));
  };

  const onError: PositionErrorCallback = (params) => {
    logData('geolocation onError', params);
    setIsLocating(false);
  };

  const requestGeolocation = () => {
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(onSuccess, onError, geolocateOptions);
  };

  const handleGeolocate = () => {
    logData('GoogleMaps - handleGeolocate - permissionState', { navigator, permissionState, isLocating });
    if (!navigator.geolocation || isLocating) return;

    logData('GoogleMaps - handleGeolocate - permissionState', { permissionState, isLocating });
    if (permissionState === 'granted') {
      requestGeolocation();
    } else {
      setShowPermissionInfo(true);
    }
  };

  useEffect(() => {
    if (coords) setCurrentCoords(coords);
  }, [coords]);

  useEffect(() => {
    if (!autoLocate || !navigator.geolocation || !isLoaded) return;

    const checkPermissionOnMount = async () => {
      if (!navigator.permissions) {
        // Sin API de permisos: intentar directamente
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(onSuccess, onError, geolocateOptions);
        return;
      }
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionState(result.state);
        if (result.state === 'granted') {
          setIsLocating(true);
          navigator.geolocation.getCurrentPosition(onSuccess, onError, geolocateOptions);
        } else {
          setShowPermissionInfo(true);
        }
        result.addEventListener('change', () => setPermissionState(result.state));
      } catch {
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(onSuccess, onError, geolocateOptions);
      }
    };

    void checkPermissionOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLocate, isLoaded]);

  const markerIcon = useMemo(() => {
    const keyCompany = provider || '';

    return markerIcons[keyCompany as keyof typeof markerIcons] ?? markerIcons['default'];
  }, [provider]);

  const craneIcon = useMemo(() => {
    const keyCompany = provider || '';

    return craneIcons[keyCompany as keyof typeof craneIcons] ?? craneIcons['default'];
  }, [provider]);

  const taxiIcon = useMemo(() => {
    const keyCompany = provider || '';

    return taxiIcons[keyCompany as keyof typeof taxiIcons] ?? taxiIcons['default'];
  }, [provider]);

  useEffect(() => {
    gmap?.addListener('dragend', () => {
      const center = gmap.getCenter();
      if (center) updateLocation(center);
    });
    gmap?.addListener('zoom_changed', () => {
      const center = gmap.getCenter();
      if (center) updateLocation(center);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmap]);

  useEffect(() => {
    logData('GoogleMaps - showLocation - useEffect - showLocation, currentCoords', { showLocation, currentCoords });
  }, [showLocation, currentCoords]);

  return isLoaded ? (
    <GoogleMapsStyles>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={currentCenter}
        zoom={zoom || DEFAULT_ZOOM}
        onLoad={(map: google.maps.Map) => {
          setGmap(map);
        }}
        onUnmount={onUnmount}
        options={options}
        id="google-map"
      >
        {/* Child components, such as markers, info windows, etc. */}
        {!!markerList &&
          markerList.map((item) => (
            <Marker
              key={`${item.coord[0]}-${item.coord[1]}`}
              icon={{ url: markerIcon }}
              position={{ lat: item.coord[0], lng: item.coord[1] }}
              onClick={item.action}
            />
          ))}

        {myCraneLocation && myCraneLocation.lat !== 0 && myCraneLocation.lng !== 0 && (
          <Marker icon={craneIcon} position={myCraneLocation} />
        )}
        {myTaxiLocation && myTaxiLocation.lat !== 0 && myTaxiLocation.lng !== 0 && (
          <Marker
            icon={{
              url: taxiIcon,
            }}
            position={myTaxiLocation}
          />
        )}
        {arrivalTime && (
          <OverlayViewDiv>
            <p>{t('components:google-maps.overlay-view', { time: arrivalTime.format('H:mm') })}</p>
          </OverlayViewDiv>
        )}
        {craneLocations?.map((crane, index: number) => (
          <Marker
            key={`${crane.latitud} ${crane.longitud} ${index}`}
            position={{ lat: crane.latitud, lng: crane.longitud }}
            icon={craneIcon}
            onClick={() => {
              if (onCraneClick) onCraneClick(crane.codigoGrua);
            }}
          />
        ))}
        {showLocation && currentCoords && (
          <Marker icon={{ url: markerIcon }} position={{ lat: currentCoords[0], lng: currentCoords[1] }} />
        )}
      </GoogleMap>
      {showCenter && (
        <CenterMarkerStyle>
          <img src={markerIcon} alt="" />
        </CenterMarkerStyle>
      )}
      {autoLocate && (
        <GeolocationButtonStyle
          onClick={handleGeolocate}
          disabled={isLocating}
          aria-label={t('components:google-maps.geolocate-button')}
          type="button"
        >
          <GeolocationIcon width={20} height={20} />
        </GeolocationButtonStyle>
      )}
      {showPermissionInfo && (
        <ModalContainer
          fullScreen={false}
          active={showPermissionInfo}
          onClose={() => setShowPermissionInfo(false)}
          title={t('components:google-maps.permission-title')}
          text={
            permissionState === 'denied'
              ? t('components:google-maps.permission-denied')
              : t('components:google-maps.permission-prompt')
          }
          buttons={
            permissionState === 'denied'
              ? [
                  {
                    className: 'secondary',
                    label: t('components:google-maps.permission-close'),
                    onClick: () => setShowPermissionInfo(false),
                  },
                ]
              : [
                  {
                    className: 'primary',
                    label: t('components:google-maps.permission-allow'),
                    onClick: () => {
                      setShowPermissionInfo(false);
                      requestGeolocation();
                    },
                  },
                  {
                    className: 'secondary',
                    label: t('components:google-maps.permission-cancel'),
                    onClick: () => setShowPermissionInfo(false),
                  },
                ]
          }
        />
      )}
    </GoogleMapsStyles>
  ) : (
    <></>
  );
};

export default memo(GoogleMaps);
